import { useEffect } from 'react';
import * as Tabs from '@radix-ui/react-tabs';
import { useStore } from '@/state/store';
import { ComplexityToggle } from '@/components/ComplexityToggle';
import { ThemeToggle } from '@/components/ThemeToggle';
import { DataBadges } from '@/components/DataBadges';
import { InputsPanel } from '@/components/InputsPanel';
import { KpiCards } from '@/components/KpiCards';
import { Narrative, WarningsBanner } from '@/components/Narrative';
import { ScenarioLibrary } from '@/components/ScenarioLibrary';
import { CompareView } from '@/components/CompareView';
import { Templates } from '@/components/Templates';
import { Glossary } from '@/components/Glossary';
import { Onboarding, HelpButton } from '@/components/Onboarding';
import { ExportButtons } from '@/components/ExportButtons';
import { PrintReport } from '@/print/PrintReport';
import { SolverPanel } from '@/components/SolverPanel';
import { SensitivityPanel } from '@/components/SensitivityPanel';
import { MonteCarloPanel } from '@/components/MonteCarloPanel';
import { StressTestPanel } from '@/components/StressTestPanel';
import {
  AprChart,
  BalanceChart,
  BorrowVsSellChart,
  CumulativeInterestChart,
  EstateChart,
  LtvChart,
  MonthlyInterestChart,
  NetWorthChart,
} from '@/components/charts/projectionCharts';

function useThemeEffect() {
  const theme = useStore((s) => s.ui.theme);
  useEffect(() => {
    const root = document.documentElement;
    const apply = () => {
      const dark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
      root.classList.toggle('dark', dark);
    };
    apply();
    if (theme === 'system') {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      mq.addEventListener('change', apply);
      return () => mq.removeEventListener('change', apply);
    }
    return undefined;
  }, [theme]);
}

const DISCLAIMER =
  'This tool is provided for general informational and educational purposes only. It does not constitute, and must not be relied upon as, financial, investment, tax, accounting, or legal advice, or a recommendation to enter into any transaction. All outputs are hypothetical projections based on the assumptions you enter and may not reflect actual results, fees, or a lender’s exact terms. A securities-backed line of credit is typically a demand loan the lender can call at any time; benchmark rates, spreads, advance rates, and maintenance requirements can change, and a market decline can force taxable liquidation and losses. Tax, estate, and step-up-in-basis figures are user assumptions that vary by situation and jurisdiction and may change with law. No warranty is made as to accuracy or completeness. Consult qualified financial, tax, and legal professionals before making any decision. Use at your own risk.';

const tabTrigger = 'rounded px-3 py-1 text-sm data-[state=active]:bg-sky-600 data-[state=active]:text-white';

export default function App() {
  const init = useStore((s) => s.init);
  const result = useStore((s) => s.result);
  const loaded = useStore((s) => s.loaded);
  useThemeEffect();

  useEffect(() => {
    void init();
  }, [init]);

  return (
    <div className="min-h-full bg-slate-50 text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <Onboarding />
      <header className="no-print sticky top-0 z-20 border-b border-slate-200 bg-white/90 backdrop-blur dark:border-slate-700 dark:bg-slate-900/90">
        <div className="mx-auto max-w-7xl px-4 py-2">
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
            <h1 className="text-lg font-bold tracking-tight">Securities-Backed Borrowing Dashboard</h1>
            <div className="flex flex-wrap items-center gap-2">
              <ComplexityToggle />
              <Templates />
              <ScenarioLibrary />
              <Glossary />
              <ExportButtons />
              <HelpButton />
              <ThemeToggle />
            </div>
          </div>
          {/* Tagline spans the full header width: one line when it fits, wraps only when narrow. */}
          <p className="mt-0.5 text-xs leading-snug text-slate-500 dark:text-slate-400">
            Borrow against your investments instead of selling — see the true cost, risk, and long-term
            wealth impact, at whatever rate your lender quotes.
          </p>
          <div className="mt-1">
            <DataBadges />
          </div>
          <p className="mt-1 text-[11px] font-medium text-caution-surface">
            Educational tool only — not financial, investment, tax, or legal advice. Outputs are
            hypothetical estimates, not guarantees.
          </p>
        </div>
      </header>

      <main className="no-print mx-auto grid max-w-7xl grid-cols-1 gap-4 p-4 lg:grid-cols-[360px_1fr]">
        <aside data-tour="inputs" className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm dark:border-slate-700 dark:bg-slate-800">
          <h2 className="mb-2 text-sm font-semibold">Assumptions</h2>
          {loaded ? <InputsPanel /> : <p className="text-sm text-slate-500">Loading…</p>}
        </aside>

        <section className="flex flex-col gap-4">
          <Tabs.Root defaultValue="dashboard">
            <Tabs.List data-tour="views" className="inline-flex gap-1 rounded-md border border-slate-300 p-0.5 dark:border-slate-600" aria-label="Results view">
              <Tabs.Trigger value="dashboard" className={tabTrigger}>Dashboard</Tabs.Trigger>
              <Tabs.Trigger value="compare" className={tabTrigger}>Compare</Tabs.Trigger>
              <Tabs.Trigger value="analysis" className={tabTrigger}>Analysis</Tabs.Trigger>
              <Tabs.Trigger value="stress" className={tabTrigger}>Stress</Tabs.Trigger>
            </Tabs.List>

            <Tabs.Content value="dashboard" className="mt-4 flex flex-col gap-4">
              <div data-tour="kpis"><KpiCards /></div>
              <WarningsBanner />
              <Narrative />
              {result && (
                <div data-tour="charts" className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                  <NetWorthChart />
                  <BorrowVsSellChart />
                  <BalanceChart />
                  <LtvChart />
                  <MonthlyInterestChart />
                  <CumulativeInterestChart />
                  <AprChart />
                  <EstateChart />
                </div>
              )}
            </Tabs.Content>

            <Tabs.Content value="compare" className="mt-4">
              <CompareView />
            </Tabs.Content>

            <Tabs.Content value="analysis" className="mt-4 grid grid-cols-1 gap-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <SolverPanel />
                <SensitivityPanel />
              </div>
              <MonteCarloPanel />
            </Tabs.Content>

            <Tabs.Content value="stress" className="mt-4 max-w-xl">
              <StressTestPanel />
            </Tabs.Content>
          </Tabs.Root>
        </section>
      </main>

      <footer className="no-print mx-auto max-w-7xl px-4 py-6 text-xs text-slate-500 dark:text-slate-400">{DISCLAIMER}</footer>

      <PrintReport />
    </div>
  );
}
